import { useEffect, useRef, useState } from "react";
import { Play, Scissors, Merge, Trash2, Plus, Minus, Flag } from "lucide-react";
import { fmtShort, parseTime } from "@/lib/subtitles";
import { hasWordTiming } from "@/lib/lines";

const TimeField = ({ label, value, onCommit, testid }) => {
  const [v, setV] = useState(fmtShort(value));
  useEffect(() => setV(fmtShort(value)), [value]);
  const commit = () => {
    const t = parseTime(v);
    if (t == null || t < 0) setV(fmtShort(value));
    else onCommit(+t.toFixed(3));
  };
  return (
    <div className="flex items-center gap-0.5">
      <button aria-label={`${label} minus 0.1 seconds`} onClick={() => onCommit(+Math.max(0, value - 0.1).toFixed(3))} data-testid={`${testid}-minus`}
        className="h-9 w-8 grid place-items-center rounded hover:bg-[var(--cc-hi)] text-[var(--cc-text-2)]"><Minus className="h-3.5 w-3.5" /></button>
      <input aria-label={label} value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        data-testid={testid} className="w-[68px] h-9 text-center font-mono-ts text-xs text-amber-400 bg-[var(--cc-bg)] border border-[var(--cc-border-strong)] rounded" />
      <button aria-label={`${label} plus 0.1 seconds`} onClick={() => onCommit(+(value + 0.1).toFixed(3))} data-testid={`${testid}-plus`}
        className="h-9 w-8 grid place-items-center rounded hover:bg-[var(--cc-hi)] text-[var(--cc-text-2)]"><Plus className="h-3.5 w-3.5" /></button>
    </div>
  );
};

const Action = ({ icon: Icon, label, onClick, testid, danger }) => (
  <button onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className={`h-10 px-2.5 rounded-md inline-flex items-center gap-1.5 text-xs font-semibold hover:bg-[var(--cc-hi)] ${danger ? "text-rose-300" : "text-[var(--cc-text-2)] hover:text-white"}`}>
    <Icon className="h-4 w-4" /><span className="hidden sm:inline">{label}</span>
  </button>
);

export const CaptionItem = ({ c, i, active, selected, isLast, onChange, onSeek, onSplit, onMerge, onDelete, onAdd, onSelect, itemRef }) => {
  const ta = useRef(null);
  const dur = c.end - c.start;
  return (
    <li ref={itemRef} data-testid={`caption-item-${i}`} onFocus={onSelect}
      className={`rounded-lg border p-2.5 transition-colors ${active ? "border-[var(--cc-accent)] bg-amber-500/5" : selected ? "border-[var(--cc-border-strong)] bg-[var(--cc-elev)]" : "border-[var(--cc-border)] bg-[var(--cc-panel)]"}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-mono-ts text-[var(--cc-muted)]">#{i + 1}
          {hasWordTiming(c) && <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-sans font-semibold" data-testid={`caption-word-timing-${i}`}>word timing</span>}
        </span>
        <span className="text-[11px] font-mono-ts text-[var(--cc-text-2)]" data-testid={`caption-duration-${i}`}>{dur.toFixed(2)}s</span>
      </div>
      <div className="flex items-center justify-between gap-1 mb-2">
        <TimeField label="Start time" value={c.start} onCommit={(start) => onChange({ start: Math.min(start, c.end - 0.1) })} testid={`caption-start-${i}`} />
        <span className="text-[var(--cc-muted)] text-xs">→</span>
        <TimeField label="End time" value={c.end} onCommit={(end) => onChange({ end: Math.max(end, c.start + 0.1) })} testid={`caption-end-${i}`} />
      </div>
      <textarea ref={ta} value={c.text} rows={2} lang="hi" aria-label={`Caption ${i + 1} text`} data-testid={`caption-text-${i}`}
        onChange={(e) => onChange({ text: e.target.value })}
        className="w-full rounded-md bg-[var(--cc-bg)] border border-[var(--cc-border-strong)] p-2.5 text-[15px] deva resize-y focus:border-[var(--cc-accent)] outline-none" />
      {c.flag && <p className="text-xs text-rose-300 mt-1 flex items-center gap-1" data-testid={`caption-flag-${i}`}><Flag className="h-3 w-3" /> {c.flag}</p>}
      <div className="flex flex-wrap items-center gap-0.5 mt-1.5 -ml-1">
        <Action icon={Play} label="Play" onClick={onSeek} testid={`caption-play-${i}`} />
        <Action icon={Scissors} label="Split at cursor" onClick={() => onSplit(ta.current?.selectionStart ?? 0)} testid={`caption-split-${i}`} />
        {!isLast && <Action icon={Merge} label="Merge next" onClick={onMerge} testid={`caption-merge-${i}`} />}
        <Action icon={Plus} label="Add after" onClick={onAdd} testid={`caption-add-after-${i}`} />
        <Action icon={Trash2} label="Delete" onClick={onDelete} testid={`caption-delete-${i}`} danger />
      </div>
    </li>
  );
};
